// ─────────────────────────────────────────────────────────────
// SortBeat Daily — Core Game Types
// ─────────────────────────────────────────────────────────────

/** The 6 possible token colours. Maps to a hex palette in theme. */
export type Color = 'red' | 'blue' | 'green' | 'yellow' | 'purple' | 'orange';

/** All 6 colours as a constant array for random selection / seed use. */
export const ALL_COLORS: Color[] = ['red', 'blue', 'green', 'yellow', 'purple', 'orange'];

/** A single item that falls down a lane. */
export interface Item {
  id: string;
  color: Color;
  /** Which of the 6 lanes this item belongs to (0-indexed). */
  laneIndex: number;
  /** Row position in the grid (0 = top, MAX_ROWS-1 = bottom). */
  row: number;
  /** Whether this item has been placed (landed) or is still in flight. */
  placed: boolean;
}

/** A detected chain of same-colour items in the same lane. */
export interface Chain {
  laneIndex: number;
  /** Row index of the topmost item in the chain. */
  startRow: number;
  /** Number of consecutive same-colour items. */
  length: number;
  color: Color;
  /** Item IDs that form this chain. */
  itemIds: string[];
}

/** A single cell in the grid. null means empty. */
export type GridCell = Color | null;

/** The full 6×12 game grid — [laneIndex][row], row 0 = bottom. */
export type Grid = GridCell[][];

/** Grid dimensions. */
export const LANE_COUNT = 6;
export const ROW_COUNT = 12;
export const MIN_CHAIN_LENGTH = 3;

/** A burst particle emitted during a chain clear animation. */
export interface Particle {
  id: string;
  x: number;
  y: number;
  color: Color;
  /** Velocity components for animation worklet. */
  vx: number;
  vy: number;
}

/** A move recorded for ghost replay. */
export interface MoveRecord {
  laneIndex: number;
  /** Milliseconds from game start. */
  timestamp: number;
  color: Color;
}

/** Result submitted to Supabase after game over. */
export interface GameResult {
  score: number;
  chainsCleared: number;
  onBeatCount: number;
  maxCombo: number;
  totalMoves: number;
  moves: MoveRecord[];
  /** ISO date string YYYY-MM-DD for the daily challenge. */
  challengeDate: string;
}

/** Emoji receipt output for social sharing. */
export interface ShareReceipt {
  emojiGrid: string;
  score: number;
  chainsCleared: number;
  onBeatCount: number;
  /** Percentage of chains cleared on-beat (0–100, rounded). 0 when chainsCleared = 0. */
  beatRate: number;
  themeName: string;
  challengeDate: string;
  /**
   * A single headline "best moment" brag line (e.g. "🔥 x7 combo streak").
   * The viral unit is a moment, not just a score — this is surfaced on the
   * ShareCard and embedded in the emoji receipt (reviewer note 2026-07-07).
   */
  bestMoment: string;
}

/** An audio skin — swappable background track with visual accent. */
export interface AudioSkin {
  id: string;
  name: string;
  /** Require() path to the audio file in assets/audio/. */
  trackAsset: number;
  /** BPM of the track — drives beat sync timing. */
  bpm: number;
  /** Hex accent colour used for UI highlights. */
  accentColor: string;
  /** Whether this skin is unlocked (free) or requires IAP. */
  unlocked: boolean;
  /**
   * Chaos Track Pack (docs/spec.md §3.1 Phase 7.4) — a Remix Mode cosmetic
   * variant authored with its own riser/drop cues. Purely a SkinStore label;
   * never gates gameplay, never a separate purchase flow from other skins.
   */
  chaosPack?: boolean;
  /**
   * Seasonal skin (e.g. Halloween) — same IAP pattern as other skins, just
   * flagged for a "SEASONAL" badge in the SkinStore UI. Never time-gates
   * purchase/use in code; any availability-window decision is a store
   * listing concern, not a game-logic one.
   */
  seasonal?: boolean;
}

/** The Zustand game store shape. */
export interface GameState {
  // ── Grid ──────────────────────────────────────────────────
  grid: Grid;
  /** Items currently in flight (animating downward). */
  fallingItems: Item[];
  /** Upcoming item colours the player can see. */
  queue: Color[];

  // ── Scoring ───────────────────────────────────────────────
  score: number;
  combo: number;
  maxCombo: number;
  chainsCleared: number;
  onBeatCount: number;
  totalMoves: number;

  // ── Game lifecycle ─────────────────────────────────────────
  status: 'idle' | 'playing' | 'paused' | 'gameover';
  /** True once the player has used their one ad-continue for this session. */
  continueUsed: boolean;

  // ── Audio ─────────────────────────────────────────────────
  activeSkin: AudioSkin;
  /** Current beat index within the loop (0-based). */
  currentBeat: number;

  // ── Replay ────────────────────────────────────────────────
  moves: MoveRecord[];
  startedAt: number | null;

  // ── Explosion events ──────────────────────────────────────
  /**
   * Chains cleared on the last drop — the animation layer reads these to
   * play the burst explosion, then calls clearExplosions() when done.
   */
  pendingExplosions: Chain[];

  // ── Actions ───────────────────────────────────────────────
  /**
   * @param callResponseBonus Remix Mode only (docs/spec.md §3.1 Phase 7.3) —
   * true when the drop landed on-beat in the currently-flashing
   * Call-and-Response lane. Ignored by the Daily Challenge store.
   */
  dropItem: (laneIndex: number, wasOnBeat?: boolean, callResponseBonus?: boolean) => void;
  startGame: (seed: string) => void;
  pauseGame: () => void;
  resumeGame: () => void;
  resetGame: () => void;
  setCurrentBeat: (beat: number) => void;
  clearExplosions: () => void;
  /** Switch the active audio skin by id. No-ops if the skin is locked. */
  setActiveSkin: (skinId: string) => void;
  /**
   * Ad-continue reprieve: clears the fullest lane and resumes the game.
   * Can only be used once per session — guarded by `continueUsed`.
   */
  continueGame: () => void;
  /**
   * Remix Mode only — the top-tier (Director tier 8) payoff. A real,
   * consequential reward (score bonus + board-relief wipe), not just a flash.
   * Optional because the Daily store has no Director tier system.
   */
  encoreBurst?: () => void;
}

/** Daily challenge fetched from Supabase. */
export interface DailyChallenge {
  id: string;
  /** ISO date YYYY-MM-DD */
  date: string;
  /** Deterministic seed string derived from the date. */
  seed: string;
  themeName: string;
}

/** A leaderboard entry — anonymised, no user_id exposed (No PII rule). */
export interface LeaderboardEntry {
  rank: number;
  score: number;
  chainsCleared: number;
  maxCombo: number;
  onBeatCount: number;
  submittedAt: string;
}
